import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiErr } from "@/lib/api";
import { useCompanyName } from "@/lib/company";
import { APP_NAME } from "@/config";
import { Page, PageHeader, Card, Label11, Loading, usd } from "@/components/common";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogContent, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Loader2, ExternalLink, TriangleAlert, Check, Info } from "lucide-react";
import { useFlags } from "@/lib/flags";
import { NOT_REFUNDABLE } from "@/config";
import { useSeats, SeatsCard } from "@/components/Seats";
import { cn } from "@/lib/utils";
import ConfirmDestructive from "@/components/ConfirmDestructive";
import PlanCard, { serverLine } from "@/components/PlanCard";
import BillingHistory from "@/components/BillingHistory";
import { minus } from "@/lib/documents";

// design_handoff_crewbench/screens/Store.dc.html: the founder's plan, workspace server and credits (FEATURE_STORE).
// Buying goes to Lemon Squeezy's own checkout page (a signed URL the backend asked for); it comes back here with
// ?paid=<purchase>, and the page waits for the webhook. The browser never grants anything. With the AI wallet
// (FEATURE_WALLET, backend/wallet.py; design_handoff_team_roles/screens/Store.dc.html): one balance whose credits never
// expire, and Refund my credits. Billing history lists every payment with its invoice (components/BillingHistory.jsx).
const day = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long" }) : "—");
const dayYear = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "—");
const SERVER_STATUS = {
  requested: "Being set up", creating: "Being set up", installing: "Being set up", failed: "Being set up",
  online: "Online", offline: "Offline", ending: "Closing", ready_to_delete: "Closed",
};
const WAIT_MS = 90_000;  // Lemon Squeezy's webhook usually arrives within seconds; it retries for about 2.5 minutes
const QUIET = "text-sm text-muted-foreground transition-colors hover:text-gr-danger";

const LEDGER = { plan_grant: "Plan credits", plan_upgrade: "Upgrade credits", seat_grant: "Seat bonus", topup: "Top-up", refund: "Refund", chargeback: "Chargeback",
  debt_paid: "Taken off your refund" };
const shortDay = (iso) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
const signed = (n) => (n > 0 ? `+${usd(n)}` : n < 0 ? `−${usd(-n)}` : usd(0));

// Refund my credits: what comes back, payment by payment, newest first (GET /store/wallet/refund)
function RefundDialog({ open, onOpenChange }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["wallet-refund"], enabled: open, staleTime: 0,
                       queryFn: async () => (await api.get("/store/wallet/refund")).data });
  const refund = useMutation({
    mutationFn: async (password) => (await api.post("/store/wallet/refund", { password })).data,
    onSuccess: ({ refund_usd }) => {
      onOpenChange(false); qc.invalidateQueries({ queryKey: ["store"] });
      toast.success(`Refund started. ${usd(refund_usd)} is on its way back to your card.`);
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  const d = q.data;
  const row = (label, value, sub, strong) => (
    <div key={label + (sub || "")} className={cn("flex justify-between gap-3 py-2 border-t first:border-t-0", strong && "font-semibold")}>
      <span className="flex flex-col"><span>{label}</span>{sub && <span className="text-sm text-muted-foreground">{sub}</span>}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
  return (
    <ConfirmDestructive open={open} onOpenChange={onOpenChange} title="Refund my credits?" pending={refund.isPending || q.isLoading}
      action={d ? `Refund ${usd(d.refund_usd)}` : "Refund"} onConfirm={(password) => refund.mutate(password)}>
      <p className="m-0 [text-wrap:pretty]">Your AI wallet's balance goes back to the cards you paid with, newest payment first. Your plan keeps running. Sessions on {APP_NAME} credits stop until you top up or switch to your own key.</p>
      {q.isLoading ? <p className="m-0 flex items-center gap-1.5"><Loader2 className="w-4 h-4 animate-spin" />Working out your refund…</p>
        : q.isError ? <p className="m-0 text-gr-danger">{apiErr(q.error)}</p> : d && (
          <div className="flex flex-col px-3.5 py-1 rounded-lg border text-sm" data-testid="wallet-refund-breakdown">
            {row("Wallet balance", usd(d.balance_usd))}
            {d.payments.map((p) => row(`From the ${shortDay(p.paid_at)} ${p.kind === "topup" ? "top-up" : "plan payment"}`, usd(p.refund_usd),
              `Card payment of ${usd(p.paid_usd)}`))}
            {row("Payment provider's fee on this refund", usd(d.fee_usd))}
            {d.kept_usd > 0 && row("Stays in your wallet", usd(d.kept_usd), "No payment stands behind it")}
            {row("Comes back to you", usd(d.refund_usd), null, true)}
          </div>
        )}
      <p className="m-0 flex items-start gap-2 text-sm"><Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />{NOT_REFUNDABLE}</p>
    </ConfirmDestructive>
  );
}

function SummaryCard({ label, children, testid }) {
  return (
    <Card className="p-[18px] flex flex-col gap-2" data-testid={testid}>
      <Label11>{label}</Label11>{children}
    </Card>
  );
}

// The AI wallet: one balance, this month's ins and outs, Top up and Refund my credits
function WalletCard({ data, balance, onRefund }) {
  const w = data.wallet || { entries: [], usage_usd: 0 };
  const month = new Date().toLocaleDateString("en-GB", { month: "long" });
  const last = w.last_refund;
  const recent = last?.status === "done" && Date.now() - new Date(last.at).getTime() < 10 * 86400000;
  return (
    <Card className="p-[18px] flex flex-col gap-3" data-testid="store-wallet">
      <div className="flex flex-col gap-1">
        <Label11>AI wallet</Label11>
        {balance < 0 ? (  // a scheduled investor update ran on credit (design_handoff_founder_assistant screen 9)
          <>
            <span className="font-display text-[28px] leading-none tabular-nums text-gr-warning" data-testid="wallet-owed">{minus(-balance)} owed</span>
            <span className="text-sm text-muted-foreground">Your next top-up, plan renewal or seat credits cover this first.</span>
          </>
        ) : (
          <>
            <span className="text-[32px] leading-none tabular-nums">{usd(balance)}</span>
            <span className="flex items-center gap-1.5 text-sm text-gr-success"><Check className="w-3.5 h-3.5" />Credits never expire. Refundable any time.</span>
          </>
        )}
      </div>
      {last?.status === "refunding" && <p className="m-0 text-sm text-gr-warning">A refund of your credits is under way.</p>}
      {recent && <p role="status" className="m-0 px-3 py-2 rounded-lg border border-gr-success text-sm">{usd(last.refund_usd)} is on its way back to your card, up to 10 days.</p>}
      <div className="flex flex-col text-sm tabular-nums">
        <span className="pb-1"><Label11>{month}</Label11></span>
        {w.entries.map((e, i) => (
          <div key={i} className="flex justify-between gap-3 py-1.5 border-t"><span>{LEDGER[e.kind] || e.kind}, {shortDay(e.at)}</span><span>{signed(e.amount_usd)}</span></div>
        ))}
        <div className="flex justify-between gap-3 py-1.5 border-t"><span>Usage</span><span>{signed(-w.usage_usd)}</span></div>
      </div>
      <div className="mt-auto flex flex-wrap gap-2">
        <Button asChild><a href="#topup">{balance < 0 ? "Add credits" : "Top up"}</a></Button>
        <Button variant="outline" aria-disabled={balance <= 0 || last?.status === "refunding"} disabled={balance <= 0 || last?.status === "refunding"}
          onClick={onRefund} data-testid="wallet-refund">Refund my credits</Button>
      </div>
    </Card>
  );
}

export default function OwnerStore() {
  const company = useCompanyName();
  const [params, setParams] = useSearchParams();
  const paid = params.get("paid");
  const started = useRef(Date.now());
  const [waiting, setWaiting] = useState(!!paid);
  const [changing, setChanging] = useState(null);  // {plan, since}: a plan change asked, until its webhook shows it
  const [settling, setSettling] = useState(0);  // when Cancel plan / Keep my plan was asked: its webhook follows
  const [cancelling, setCancelling] = useState(false);
  const [endingNow, setEndingNow] = useState(false);
  const [asking, setAsking] = useState(null);  // the plan a change is being confirmed for
  const [refunding, setRefunding] = useState(false);
  const wallet = useFlags().wallet;
  const seats = useSeats();  // the plan's seats and slots (backend/seats.py)
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["store", paid], queryFn: async () => (await api.get("/store", { params: paid ? { purchase: paid } : {} })).data,
    refetchInterval: waiting || changing || Date.now() - settling < 30_000 ? 3000 : false,
  });
  const cancel = useMutation({  // docs/ACCOUNT_LIFECYCLE_PLAN.md L1: runs to the end of the period, then ends
    mutationFn: async (password) => (await api.post("/store/cancel", { password })).data,
    onSuccess: ({ ends_at }) => {
      setCancelling(false); setSettling(Date.now()); qc.invalidateQueries({ queryKey: ["store"] });
      toast.success(`Your plan is cancelled. It keeps working until ${day(ends_at)}.`);
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  const quote = useQuery({  // what "End now" refunds: worked out by the backend, shown before confirming
    queryKey: ["end-now-quote"], queryFn: async () => (await api.get("/store/end-now")).data, enabled: endingNow, staleTime: 0,
  });
  const endNow = useMutation({  // docs/ACCOUNT_LIFECYCLE_PLAN.md L3: the unused part refunded, the plan ended at once
    mutationFn: async (password) => (await api.post("/store/end-now", { password })).data,
    onSuccess: ({ refund_usd }) => {
      setEndingNow(false); qc.invalidateQueries({ queryKey: ["store"] });
      toast.success(refund_usd > 0 ? `Your plan has ended. ${usd(refund_usd)} is on its way back to your card.` : "Your plan has ended.");
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  const resume = useMutation({
    mutationFn: async () => (await api.post("/store/resume")).data,
    onSuccess: () => {
      setSettling(Date.now()); qc.invalidateQueries({ queryKey: ["store"] });
      toast.success(`Your plan renews again on ${day(data?.subscription?.ends_at || data?.subscription?.renews_at)}.`);
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  // Change plan: Lemon Squeezy switches the subscription (never in its portal: the store is shared with another app).
  const change = useMutation({
    mutationFn: async (plan) => (await api.post("/store/change-plan", { plan_id: plan.id })).data,
    onSuccess: (got, plan) => {
      setAsking(null);
      if (got.portal_url) {  // PayPal: Lemon Squeezy's API can't change it, the owner confirms on its page
        window.open(got.portal_url, "_blank", "noopener,noreferrer");
        toast.info("Your plan is paid with PayPal: confirm the change on Lemon Squeezy's page.");
      } else toast.success(`Lemon Squeezy is changing your plan to ${plan.name}. It shows here in a few seconds.`);
      setChanging({ plan: plan.id, since: Date.now() });
    },
    onError: (e) => toast.error(apiErr(e)),
  });
  useEffect(() => {
    if (!changing || !data) return;
    if (data.subscription?.plan_id === changing.plan) {
      setChanging(null);
      toast.success(`You're on the ${data.subscription.plan} plan.`);
    } else if (Date.now() - changing.since > WAIT_MS) {
      setChanging(null);
      toast.info("Lemon Squeezy is still confirming your plan change. It shows here once it does.");
    }
  }, [changing, data]);
  const status = data?.purchase?.status;
  useEffect(() => {  // back from the checkout: until the webhook marks the purchase paid (or a minute and a half)
    if (!paid || !waiting || !data) return;
    // a plan: its subscription too, and its first invoice's credits (they arrive a few seconds after the order)
    const planReady = data.subscription && (data.account?.plan_period_end || data.account?.plan_usd > 0);
    if (status === "paid" && (data.purchase.kind !== "plan" || planReady)) {
      setWaiting(false);
      qc.invalidateQueries({ queryKey: ["billing-history"] });
      toast.success(data.purchase.kind === "plan" ? "Your plan is active." : "Your credits are here.");
      setParams({}, { replace: true });
    } else if (status === "mismatch" || Date.now() - started.current > WAIT_MS) {
      setWaiting(false);
      toast.info("Your payment is being checked. If it was taken, it will appear here soon; we'll sort it out otherwise.");
      setParams({}, { replace: true });
    }
  }, [paid, waiting, data, status, setParams, qc]);
  const buy = useMutation({
    mutationFn: async ({ kind, item_id }) => (await api.post("/store/checkout", { kind, item_id })).data,
    onSuccess: ({ url }) => window.location.assign(url),  // Lemon Squeezy's checkout page
    onError: (e) => toast.error(apiErr(e)),
  });
  const portal = useMutation({
    mutationFn: async () => (await api.post("/store/portal")).data,
    onSuccess: ({ url }) => window.open(url, "_blank", "noopener,noreferrer"),
    onError: (e) => (e?.response?.status === 404 ? toast.info(apiErr(e)) : toast.error(apiErr(e))),
  });

  const manage = data?.billing_portal && (
    <Button variant="outline" onClick={() => portal.mutate()} disabled={portal.isPending} data-testid="manage-billing">
      {portal.isPending ? <Loader2 className="animate-spin" /> : <ExternalLink />}Manage billing
    </Button>
  );
  const title = wallet ? "Your plan, workspace server and AI wallet." : `Your plan, workspace server and ${APP_NAME} credits.`;
  if (isLoading || !data) return <Page><PageHeader eyebrow="Store" title={title} /><Loading /></Page>;

  const sub = data.subscription;
  const acct = data.account;
  const balance = acct?.balance_usd ?? 0;
  const ending = sub?.status === "cancelled" && sub.ends_at;
  const running = sub && ["active", "on_trial"].includes(sub.status);
  const current = data.plans.find((p) => p.id === sub?.plan_id);
  const grace = data.server_grace_days;
  const renewal = day(sub?.renews_at);
  const q = quote.data;

  return (
    <Page data-testid="owner-store">
      <PageHeader eyebrow={`Store · ${sub?.plan ? `${sub.plan} plan` : "No plan"}`} title={title}
        status={balance < 0 ? `${minus(-balance)} owed: your next credits cover it first.`
          : wallet ? `${usd(balance)} in your AI wallet.` : `${usd(balance)} of credits left.`} actions={manage} />
      {(waiting || changing) && (
        <Card className="px-5 py-4 flex items-center gap-2" data-testid={waiting ? "payment-received" : "plan-changing"}>
          <Loader2 className="w-4 h-4 animate-spin shrink-0" />
          {waiting ? "Confirming your payment with Lemon Squeezy; this usually takes a few seconds." : "Lemon Squeezy is changing your plan; this usually takes a few seconds."}
        </Card>
      )}

      <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-4 items-stretch">
        <SummaryCard label="Plan" testid="store-plan">
          <span className="font-display text-[22px] leading-[1.25] tracking-[0.02em] font-semibold">{sub ? sub.plan || "Your plan" : "No plan"}</span>
          {!sub ? <span className="text-sm text-muted-foreground">Use your own API keys and server, or choose a plan below.</span>
            : ["past_due", "unpaid"].includes(sub.status) ? (
              <span className="flex items-start gap-1.5 text-sm text-gr-warning"><TriangleAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />Payment failed: update your card in Manage billing.</span>
            ) : ending ? (
              <span className="flex items-start gap-1.5 text-sm text-gr-warning"><TriangleAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />Ends on {day(sub.ends_at)}. It isn't charged again.</span>
            ) : <span className="text-sm text-muted-foreground">{current ? `${current.display_price.replace(/\/month$/, " a month")}. ` : ""}Renews on {renewal}.</span>}
          {sub && (ending || running) && (
            <div className="mt-auto pt-2 flex flex-wrap items-center gap-x-4 gap-y-2">
              {ending && <Button variant="outline" onClick={() => resume.mutate()} disabled={resume.isPending} data-testid="keep-plan">
                {resume.isPending && <Loader2 className="animate-spin" />}Keep my plan</Button>}
              {running && <button type="button" className={QUIET} onClick={() => setCancelling(true)} data-testid="cancel-plan">Cancel plan</button>}
              <button type="button" className={QUIET} onClick={() => setEndingNow(true)} data-testid="end-plan-now">End now with a refund</button>
            </div>
          )}
        </SummaryCard>
        <SummaryCard label="Workspace server" testid="store-server">
          <span className="font-display text-[22px] leading-[1.25] tracking-[0.02em] font-semibold">{data.server ? SERVER_STATUS[data.server.status] || data.server.status : "None yet"}</span>
          <span className="text-sm text-muted-foreground">
            {current?.server ? `${serverLine(current.server).replace(/, (\d+) sessions/, ": $1 sessions")}. ` : data.server?.name ? `${data.server.name}. ` : ""}
            {company ? `${company}'s` : "Your"} sessions run only on it.
          </span>
          {data.server?.deletes_on ? (
            <span className="text-sm text-gr-warning [text-wrap:pretty]" data-testid="server-deletes-on">Deleted on {day(data.server.deletes_on)}. Choose a plan before then to keep it.</span>
          ) : ending && <span className="text-sm text-gr-warning [text-wrap:pretty]">After your plan ends it's kept for {grace} days, then deleted. We email you 3 days before.</span>}
        </SummaryCard>
        {wallet ? <WalletCard data={data} balance={balance} onRefund={() => setRefunding(true)} /> : <SummaryCard label="Credit balance" testid="store-balance">
          <span className="text-[24px] leading-7 tabular-nums">{usd(balance)}</span>
          <span className="text-sm text-muted-foreground tabular-nums">Plan {usd(acct?.plan_usd)} · top-up {usd(acct?.topup_usd)}{acct?.status === "suspended" && <span className="text-gr-danger"> · suspended</span>}</span>
          <span className="text-sm text-muted-foreground tabular-nums">
            {acct?.topup_expires_at ? `Top-up credits are valid until ${dayYear(acct.topup_expires_at)}. ` : ""}Used this month: {usd(data.usage_this_month_usd)}.
          </span>
        </SummaryCard>}
      </div>

      {seats && <SeatsCard s={seats} />}

      <section className="flex flex-col gap-3">
        <span className="font-display text-[15px] tracking-[0.02em] font-semibold">Plans</span>
        {data.plans.length === 0 ? <Card className="p-5 text-muted-foreground">No plans on sale yet.</Card> : (
          <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4 items-stretch">
            {data.plans.map((p) => (
              <PlanCard key={p.id} plan={p} current={sub?.plan_id === p.id} data-testid={`plan-${p.id}`}>
                {!sub ? (
                  <Button className="w-full" disabled={buy.isPending} onClick={() => buy.mutate({ kind: "plan", item_id: p.id })} data-testid={`buy-plan-${p.id}`}>
                    {buy.isPending && buy.variables?.item_id === p.id && <Loader2 className="animate-spin" />}Choose {p.name}
                  </Button>
                ) : p.id === sub.plan_id ? null : p.change ? (
                  <Button className="w-full" variant={p.change.direction === "upgrade" ? "default" : "outline"} data-testid={`change-plan-${p.id}`}
                    disabled={!!changing || change.isPending || acct?.status === "suspended"} onClick={() => setAsking(p)}>
                    {p.change.direction === "upgrade" ? "Upgrade" : "Downgrade"} to {p.name}
                  </Button>
                ) : (
                  <span className="text-sm text-muted-foreground" data-testid={`buy-plan-${p.id}`}>{ending ? "Keep your plan first to change it." : "Sort out your plan in Manage billing first."}</span>
                )}
              </PlanCard>
            ))}
          </div>
        )}
      </section>

      <section id="topup" className="flex flex-col gap-3 scroll-mt-4">
        <div className="flex flex-col gap-0.5">
          <span className="font-display text-[15px] tracking-[0.02em] font-semibold">Top up credits</span>
          <span className="text-sm text-muted-foreground [text-wrap:pretty]">
            Credits pay for AI usage at the provider's price plus {APP_NAME}'s markup. {wallet
              ? "They go into your AI wallet: they never expire, and Refund my credits gives them back any time."
              : "Top-ups stay valid for 12 months after your latest one."}
            {data.topup_limit_usd != null && ` For now, a top-up is at most ${usd(data.topup_limit_usd, 0)}.`}
          </span>
        </div>
        {data.topups.length === 0 ? <Card className="p-5 text-muted-foreground">No top-ups on sale yet.</Card> : (
          <div className="flex flex-wrap gap-2">
            {data.topups.map((t) => {
              const over = data.topup_limit_usd != null && t.credits_usd > data.topup_limit_usd;
              return (
                <Button key={t.id} variant="outline" className="tabular-nums" disabled={over || buy.isPending || acct?.status === "suspended"}
                  onClick={() => buy.mutate({ kind: "topup", item_id: t.id })} data-testid={`buy-topup-${t.id}`}>
                  {t.display_price} · {usd(t.credits_usd)} of credits
                </Button>
              );
            })}
          </div>
        )}
      </section>

      <BillingHistory wallet={wallet} />

      <p className="m-0 pt-4 border-t text-sm text-muted-foreground [text-wrap:pretty]">
        Payments are handled by Lemon Squeezy, our reseller and merchant of record, which also emails your receipts. Choosing a plan or a top-up opens its
        secure checkout and brings you back here. Read the <a href="/refunds" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:opacity-50">refund policy</a>.
        Leaving {APP_NAME}? <Link to="/account" className="underline underline-offset-2 hover:opacity-50" data-testid="store-delete-account">Delete your account</Link> and whatever you haven't used is refunded.
        {wallet && ` ${NOT_REFUNDABLE}`}
      </p>
      {wallet && <RefundDialog open={refunding} onOpenChange={setRefunding} />}

      {asking && (
        <AlertDialog open onOpenChange={(v) => !v && setAsking(null)}>
          <AlertDialogContent className="gap-3.5">
            <AlertDialogTitle>{asking.change.direction === "upgrade" ? "Upgrade" : "Downgrade"} to {asking.name}?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="flex flex-col gap-3.5 text-base text-gr-ink-secondary" data-testid="change-plan-terms">
                <p className="m-0 [text-wrap:pretty]">{asking.change.direction === "upgrade"
                  ? `Your plan changes now. Lemon Squeezy charges your card the difference for the rest of this month, and about ${usd(asking.change.extra_credits_usd)} of extra credits arrive once that payment goes through.`
                  : `Your plan changes now, with no refund for the rest of this month. The credits you have stay until your renewal on ${renewal}.`}</p>
                <p className="m-0 [text-wrap:pretty]">
                  {asking.server && `Your workspace server moves to the ${/server/i.test(asking.server.label) ? asking.server.label.toLowerCase() : `${asking.server.label.toLowerCase()} server`}, which runs ${asking.server.max_active_sessions} sessions at once. `}
                  From {renewal}: {asking.display_price.replace(/\/month$/, " a month")}, with {usd(asking.monthly_credits_usd)} of credits every month.
                </p>
              </div>
            </AlertDialogDescription>
            <AlertDialogFooter className="pt-1">
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={(e) => { e.preventDefault(); change.mutate(asking); }} disabled={change.isPending} data-testid={`confirm-change-plan-${asking.id}`}>
                {change.isPending && <Loader2 className="animate-spin" />}{asking.change.direction === "upgrade" ? "Upgrade" : "Downgrade"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
      {sub && (
        <ConfirmDestructive open={cancelling} onOpenChange={setCancelling} title={`Cancel your ${sub.plan || ""} plan?`} cancelLabel="Keep my plan"
          action="Cancel plan" pending={cancel.isPending} onConfirm={(password) => cancel.mutate(password)}
          warning={`You can keep your plan until ${renewal}. After that this can't be undone.`}>
          <p className="m-0 [text-wrap:pretty]">Your plan keeps working until {renewal} and isn't charged again. Then {wallet ? "" : "its credits end, "}your sessions are locked with
            their work backed up, and your workspace server is kept for {grace} days, then deleted. We email you 3 days before.</p>
          <p className="m-0">{wallet ? "Your AI wallet keeps its credits, and Refund my credits gives them back any time." : "Your top-up credits stay yours."}</p>
        </ConfirmDestructive>
      )}
      {sub && (
        <ConfirmDestructive open={endingNow} onOpenChange={setEndingNow} title={`End your ${sub.plan || ""} plan now?`} cancelLabel="Keep my plan"
          action={q ? `End now${q.refund_usd + (q.wallet_refund_usd || 0) > 0 ? ` and refund ${usd(q.refund_usd + (q.wallet_refund_usd || 0))}` : ""}` : "End now"}
          pending={endNow.isPending || quote.isLoading} onConfirm={(password) => endNow.mutate(password)}>
          <p className="m-0 [text-wrap:pretty]">Your plan{wallet ? "" : ", its credits"} and your sessions stop now. Sessions are locked with their work backed up, and your
            workspace server is kept for {grace} days, then deleted. We email you 3 days before.{wallet ? " Your AI wallet is refunded too." : " Your top-up credits stay yours."}</p>
          {quote.isLoading ? <p className="m-0 flex items-center gap-1.5"><Loader2 className="w-4 h-4 animate-spin" />Working out your refund…</p>
            : quote.isError ? <p className="m-0 text-gr-danger">{apiErr(quote.error)}</p> : q && (
              wallet ? (
                <p className="m-0 [text-wrap:pretty]" data-testid="end-now-refund">You get {usd(q.refund_usd)} back for the {q.days_left} days left of your workspace server this
                  month, and {usd(q.wallet_refund_usd || 0)} from your AI wallet. Lemon Squeezy refunds your card and emails the receipt.</p>
              ) : <p className="m-0 [text-wrap:pretty]" data-testid="end-now-refund">You get {usd(q.refund_usd)} back for the {q.days_left} days left of what you paid this
                month{q.extra_usd > 0 && `, minus ${usd(q.extra_usd)} of plan credits used beyond the days used`}. Lemon Squeezy refunds your card and emails the receipt.</p>
            )}
        </ConfirmDestructive>
      )}
    </Page>
  );
}
